import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { CO2E_METHODOLOGY_SUBTITLE, GHG_SCOPE_COLORS, GHG_SCOPE_HEX } from '../../constants/carbonVisualization';
import {
  DASHBOARD_ADVISOR_CARD_GUTTER_PX,
  DASHBOARD_HERO_DESKTOP_MIN_PX,
  DASHBOARD_METRIC_ROW_MARGIN_TOP,
} from '../../constants/appShellLayout';
import ComplianceMethodologyBadge from '../carbon/ComplianceMethodologyBadge';
import PlainLanguageLabel from '../shared/PlainLanguageLabel';
import { formatInr } from '../../constants/pricingPlans';
import { DataConfidenceChip, type DataConfidenceChipProps } from './DataConfidenceChip';

export interface DashboardMsmeHighlights {
  transactionCount?: number;
  documentCount?: number;
  assessmentCount?: number;
  greenCreditsAvailable?: number;
  totalCo2SavedKg?: number;
  periodCo2SavedKg?: number;
  savingsPercentage?: number;
  costSavingsInr?: number;
  treesEquivalent?: number;
  benchmarkPerformance?: string;
  companyType?: string;
  industry?: string;
  employeeCount?: number;
}

export interface DashboardHeroProps {
  carbonScore: number;
  scoreLabel: string;
  /** When false, show Needs attention even if score ≥ 60 (CI-BUG-003). */
  onTrack?: boolean;
  scoreColor: 'success' | 'warning' | 'error' | 'default';
  carbonEmissionsKg: number;
  s1Kg: number;
  s2Kg: number;
  s3Kg: number;
  scopeSumKg: number;
  lastAssessmentLabel: string | null;
  assessmentPeriodLabel: string | null;
  forecastTCO2e: number;
  forecastCaption: string;
  hotspotDescription: string;
  dataQuality?: Pick<DataConfidenceChipProps, 'level' | 'label' | 'score' | 'hints'>;
  msmeHighlights?: DashboardMsmeHighlights;
}

function scopeBarSx(fill: string, isDark: boolean) {
  return {
    height: 8,
    borderRadius: 4,
    bgcolor: alpha(fill, isDark ? 0.22 : 0.12),
    '& .MuiLinearProgress-bar': { bgcolor: fill, borderRadius: 4 },
  };
}

function formatCompact(value: number, decimals = 1): string {
  if (!Number.isFinite(value) || value <= 0) return '0';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(decimals)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(decimals)}K`;
  return value % 1 === 0 ? String(Math.round(value)) : value.toFixed(decimals);
}

function HighlightTile({
  label,
  value,
  unit,
  caption,
}: {
  label: string;
  value: string;
  unit?: string;
  caption?: string;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        height: '100%',
        bgcolor: isDark ? alpha(theme.palette.primary.main, 0.06) : alpha(theme.palette.primary.main, 0.03),
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, mt: 0.35 }}>
        {value}
        {unit ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 600 }}>
            {unit}
          </Typography>
        ) : null}
      </Typography>
      {caption ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35, lineHeight: 1.4 }}>
          {caption}
        </Typography>
      ) : null}
    </Paper>
  );
}

export const DashboardHero: React.FC<DashboardHeroProps> = ({
  carbonScore,
  scoreLabel,
  onTrack,
  scoreColor,
  carbonEmissionsKg,
  s1Kg,
  s2Kg,
  s3Kg,
  scopeSumKg,
  lastAssessmentLabel,
  assessmentPeriodLabel,
  forecastTCO2e: _forecastTCO2e,
  forecastCaption: _forecastCaption,
  hotspotDescription,
  dataQuality,
  msmeHighlights,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const barPct = (kg: number) => (scopeSumKg > 0 ? Math.round((kg / scopeSumKg) * 100) : 0);
  const footprintT = carbonEmissionsKg > 0 ? (carbonEmissionsKg / 1000).toFixed(2) : '—';
  const compliant = onTrack ?? carbonScore >= 70;
  const highlights = msmeHighlights || {};

  const scopeRows = [
    { key: 'scope1' as const, kg: s1Kg, label: GHG_SCOPE_COLORS.scope1.label },
    { key: 'scope2' as const, kg: s2Kg, label: GHG_SCOPE_COLORS.scope2.label },
    { key: 'scope3' as const, kg: s3Kg, label: GHG_SCOPE_COLORS.scope3.label },
  ];

  const highlightTiles = [
    {
      key: 'transactions',
      label: 'Transactions tracked',
      value: String(Math.round(Number(highlights.transactionCount) || 0)),
      caption: 'Operational spend signals in workspace',
    },
    {
      key: 'documents',
      label: 'Documents processed',
      value: String(Math.round(Number(highlights.documentCount) || 0)),
      caption: 'Processed bills in Upload',
    },
    {
      key: 'assessments',
      label: 'Assessments completed',
      value: String(Math.round(Number(highlights.assessmentCount) || 0)),
      caption: 'Saved carbon footprint runs',
    },
    {
      key: 'employees',
      label: 'Workforce (MSME)',
      value: highlights.employeeCount ? String(Math.round(highlights.employeeCount)) : '—',
      caption: highlights.companyType
        ? `${highlights.companyType} enterprise${highlights.industry ? ` · ${highlights.industry}` : ''}`
        : highlights.industry || 'From company profile',
    },
    {
      key: 'saved',
      label: 'CO₂ saved',
      value: formatCompact(Number(highlights.totalCo2SavedKg) || 0),
      unit: 'kg',
      caption:
        Number(highlights.periodCo2SavedKg) > 0
          ? `${formatCompact(Number(highlights.periodCo2SavedKg))} kg this period`
          : 'Reduction actions tracked',
    },
    {
      key: 'reduction',
      label: 'Reduction achieved',
      value: Number(highlights.savingsPercentage) > 0 ? Number(highlights.savingsPercentage).toFixed(1) : '0',
      unit: '%',
      caption: highlights.benchmarkPerformance
        ? `Benchmark: ${highlights.benchmarkPerformance}`
        : 'Versus previous assessment baseline',
    },
    {
      key: 'rupees',
      label: 'Operational savings',
      value: Number(highlights.costSavingsInr) > 0 ? formatInr(Number(highlights.costSavingsInr)) : '₹0',
      caption: 'Estimated value from CO₂ reductions',
    },
    {
      key: 'credits',
      label: 'Green credits',
      value: String(Math.round(Number(highlights.greenCreditsAvailable) || 0)),
      unit: 'available',
      caption: Number(highlights.greenCreditsAvailable) > 0 ? 'Ready to allocate or retire' : 'Earn through sustainability actions',
    },
  ];

  return (
    <Card
      sx={{
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.18)}`,
        background: isDark
          ? `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.dark, 0.12)} 100%)`
          : 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 55%, #f8fafc 100%)',
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Grid container spacing={3} alignItems="center">
          <Grid
            item
            xs={12}
            lg={3}
            sx={{
              [`@media (min-width:600px) and (max-width:${DASHBOARD_HERO_DESKTOP_MIN_PX - 0.05}px)`]: {
                '&&': { flexBasis: '34%', maxWidth: '34%' },
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' } }}>
              <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                <Box
                  component="div"
                  sx={{
                    width: 112,
                    height: 112,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `conic-gradient(${theme.palette[scoreColor === 'default' ? 'primary' : scoreColor].main} ${carbonScore * 3.6}deg, ${alpha(theme.palette.divider, 0.35)} 0deg)`,
                    p: '5px',
                  }}
                  aria-hidden
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      bgcolor: 'background.paper',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h4" component="span" sx={{ fontWeight: 800, lineHeight: 1 }}>
                      {carbonScore}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      / 100
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip size="small" color={scoreColor === 'default' ? 'default' : scoreColor} label={scoreLabel} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: { sm: 'center' } }}>
                <PlainLanguageLabel termId="carbon_score">Carbon score</PlainLanguageLabel>
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            lg={5}
            sx={{
              [`@media (min-width:600px) and (max-width:${DASHBOARD_HERO_DESKTOP_MIN_PX - 0.05}px)`]: {
                '&&': { flexBasis: '66%', maxWidth: '66%' },
              },
            }}
          >
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
              <PlainLanguageLabel termId="carbon_footprint" variant="heading">
                Total footprint
              </PlainLanguageLabel>
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                columnGap: 1.5,
                rowGap: 0.5,
              }}
            >
              <Typography
                variant="h3"
                component="p"
                sx={{
                  fontWeight: 800,
                  letterSpacing: { xs: 0, sm: '-0.03em' },
                  lineHeight: 1.15,
                  m: 0,
                }}
              >
                {footprintT}
              </Typography>
              <Typography component="span" variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                <PlainLanguageLabel termId="tco2e">tCO₂e</PlainLanguageLabel>
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, maxWidth: 420 }}>
              {CO2E_METHODOLOGY_SUBTITLE}
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              <ComplianceMethodologyBadge compact />
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ gap: 1 }}>
              <Chip
                size="small"
                color={compliant ? 'success' : 'warning'}
                label={compliant ? 'On track' : 'Needs attention'}
              />
              {dataQuality ? (
                <DataConfidenceChip
                  level={dataQuality.level}
                  label={dataQuality.label}
                  score={dataQuality.score}
                  hints={dataQuality.hints}
                />
              ) : null}
            </Stack>
            {(lastAssessmentLabel || assessmentPeriodLabel) && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                {lastAssessmentLabel ? `Last saved: ${lastAssessmentLabel}` : 'Run Carbon Assessment to populate metrics.'}
                {assessmentPeriodLabel ? ` · Period: ${assessmentPeriodLabel}` : ''}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} lg={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              <PlainLanguageLabel termId="ghg" variant="heading">
                Where emissions come from
              </PlainLanguageLabel>
            </Typography>
            {scopeSumKg > 0 ? (
              <Stack spacing={1.25}>
                {scopeRows.map(({ key, kg, label }) => (
                  <Box key={key}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        <PlainLanguageLabel termId={key}>{label}</PlainLanguageLabel>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(kg / 1000).toFixed(2)} tCO₂e · {barPct(kg)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={barPct(kg)}
                      sx={scopeBarSx(GHG_SCOPE_HEX[key], isDark)}
                    />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Scope totals appear after your first saved assessment.
              </Typography>
            )}
          </Grid>
        </Grid>

        {hotspotDescription ? (
          <Box
            sx={{
              mt: 2.5,
              pt: 2,
              borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.35 : 0.8)}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Biggest hotspot
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
              {hotspotDescription}
            </Typography>
          </Box>
        ) : null}

        <Grid
          container
          spacing={1.5}
          sx={{ mt: DASHBOARD_METRIC_ROW_MARGIN_TOP, pr: { md: `${DASHBOARD_ADVISOR_CARD_GUTTER_PX}px` } }}
        >
          {highlightTiles
            .filter((tile) => ['transactions', 'documents', 'assessments', 'employees'].includes(tile.key))
            .map((tile) => (
              <Grid item xs={6} sm={3} key={tile.key}>
                <HighlightTile
                  label={tile.label}
                  value={tile.value}
                  unit={tile.unit}
                  caption={tile.caption}
                />
              </Grid>
            ))}
        </Grid>
      </CardContent>
    </Card>
  );
};
